import { DS_COLOR } from '@calm/evexia-design-tokens';
import {
    Box,
    ButtonBase,
    ButtonBaseProps,
    Theme,
    useTheme,
    alpha,
    Breakpoint,
} from '@rhg/libs/material';
import {
    NumberByBreakpoint,
    sxAppend,
    ValueByBreakpoint,
} from '@rhg/libs/material/sx';
import Image, { StaticImageData } from '@rhg/libs/next/image';
import { ReactNode, useMemo } from 'react';

const TRANSITION_DURATION = 250;
const TRANSITION_EASING = 'ease-in';

const imageContainerStyle = {
    display: 'flex',
    '& img': {
        width: '100%',
    },
    overflow: 'hidden',
};

const styleImageContainer = imageContainerStyle;
const styleImageContainer11 = {
    ...imageContainerStyle,
    '& img': {
        ...imageContainerStyle['& img'],
        aspectRatio: '1',
    },
};
const styleImageContainer32 = {
    ...imageContainerStyle,
    '& img': {
        ...imageContainerStyle['& img'],
        aspectRatio: '3 / 2',
    },
};
const styleImageOuterContainer = { position: 'relative' };
const styleImageOverlay = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1,
};
const styleIconTop = { position: 'absolute', top: 0, left: 0 };
const styleIconBottom = {
    position: 'absolute',
    bottom: 0,
    left: 0,
};
const styleIconFocus = { display: 'flex' };
const styleButton = { display: 'inline', width: '100%' };
const styleFocusedOverlay = (theme: Theme) => ({
    transition: theme.transitions.create(['background-color'], {
        duration: TRANSITION_DURATION,
        easing: TRANSITION_EASING,
    }),
    backgroundColor: alpha(DS_COLOR.BLACK_100P, 0.4),
});
const styleNotFocusedOverlay = (theme: Theme) => ({
    transition: theme.transitions.create(['background-color'], {
        duration: TRANSITION_DURATION,
        easing: TRANSITION_EASING,
    }),
    backgroundColor: 'transparent',
});
const styleFocusedImage = (theme: Theme) => ({
    transition: theme.transitions.create(['transform'], {
        duration: TRANSITION_DURATION,
        easing: TRANSITION_EASING,
    }),
    transform: 'scale(1.05)',
});
const styleNotFocusedImage = (theme: Theme) => ({
    transition: theme.transitions.create(['transform'], {
        duration: TRANSITION_DURATION,
        easing: TRANSITION_EASING,
    }),
    transform: 'scale(1)',
});

function BaseElement({
    children,
    onClick,
    sx,
    trackButton,
    onMouseEnter,
    onMouseLeave,
    onFocus,
    onBlur,
    ...buttonBaseProps
}: Partial<ButtonBaseProps>) {
    const sxProp = { ...styleButton, ...(sx ?? {}) };
    return onClick ? (
        <ButtonBase
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            onFocus={onFocus}
            onBlur={onBlur}
            onClick={onClick}
            aria-label={buttonBaseProps?.['aria-label']}
            trackButton={trackButton || 'NotMapped'}
            sx={sxProp}
            {...buttonBaseProps}
        >
            {children}
        </ButtonBase>
    ) : (
        <Box sx={sx}>{children}</Box>
    );
}

const isStaticImageData = (
    src: string | StaticImageData
): src is StaticImageData => {
    return (src as StaticImageData).width !== undefined;
};

const ImageComponent = ({
    src,
    alt,
    isFocused,
    width,
}: Pick<
    FocusableOverlaidImageProps,
    'src' | 'alt' | 'isFocused' | 'width'
>) => {
    const theme = useTheme();
    const transitionStyles = useMemo(
        () => ({
            focusedOverlay: styleFocusedOverlay(theme),
            notFocusedOverlay: styleNotFocusedOverlay(theme),
            focusedImage: styleFocusedImage(theme),
            notFocusedImage: styleNotFocusedImage(theme),
        }),
        [theme]
    );

    const style = {
        ...(isFocused
            ? transitionStyles.focusedImage
            : transitionStyles.notFocusedImage),
        objectFit: 'cover',
        height: '100%',
        width: '100%',
    } as const;

    const breakpointValues = theme.breakpoints.values;
    const nextImageSizes = useMemo(() => {
        if (!width) {
            return undefined;
        }
        if (typeof width === 'number') {
            return `${width}px`;
        }
        if (typeof width === 'string') {
            return width;
        }
        const explicitEntries = (
            Object.entries(width) as [Breakpoint, string | number][]
        )
            .map(([breakpoint, value]) => {
                const size = typeof value === 'number' ? `${value}px` : value;
                return `(min-width: ${breakpointValues[breakpoint]}) ${size}`;
            })
            .join(', ');
        return `${explicitEntries}, 100vw`;
    }, [width, breakpointValues]);

    if (isStaticImageData(src)) {
        return (
            <Image src={src} alt={alt} style={style} sizes={nextImageSizes} />
        );
    }
    return (
        <Image
            src={src}
            alt={alt}
            width={1024}
            height={1024}
            style={{ ...style, height: '100%', width: '100%' }}
            sizes={nextImageSizes}
        />
    );
};

export type FocusableOverlaidImageProps = {
    src: string | StaticImageData;
    alt: string;
    iconTop?: ReactNode;
    iconBottom?: ReactNode;
    isFocused?: boolean;
    bezel?: number;
    iconFocus?: ReactNode;
    showIconsOnFocused?: boolean;
    ratio?: '1:1' | '3:2';
    width?: Partial<ValueByBreakpoint<number | string>>;
    maxHeight?: NumberByBreakpoint;
    imageSideRadiusEnabled?: boolean;
} & Partial<ButtonBaseProps>;
export default function FocusableOverlaidImage({
    src,
    alt,
    iconTop,
    iconBottom,
    isFocused = false,
    bezel = 0,
    iconFocus,
    ratio,
    maxHeight,
    width,
    showIconsOnFocused,
    imageSideRadiusEnabled = true,
    ...buttonBaseProps
}: FocusableOverlaidImageProps) {
    const theme = useTheme();

    const transitionStyles = useMemo(
        () => ({
            focusedOverlay: styleFocusedOverlay(theme),
            notFocusedOverlay: styleNotFocusedOverlay(theme),
            focusedImage: styleFocusedImage(theme),
            notFocusedImage: styleNotFocusedImage(theme),
        }),
        [theme]
    );

    const imageStyle = sxAppend(
        ratio === '1:1'
            ? styleImageContainer11
            : ratio === '3:2'
            ? styleImageContainer32
            : styleImageContainer,
        bezel && {
            borderTopLeftRadius: `${bezel}px`,
            borderBottomLeftRadius: `${bezel}px`,
            borderTopRightRadius: imageSideRadiusEnabled ? `${bezel}px` : 0,
            borderBottomRightRadius: imageSideRadiusEnabled ? `${bezel}px` : 0,
        },
        maxHeight && { maxHeight }
    );
    return (
        <BaseElement {...buttonBaseProps}>
            <Box sx={styleImageOuterContainer}>
                <Box
                    sx={sxAppend(
                        styleImageOverlay,
                        bezel && {
                            borderTopLeftRadius: `${bezel}px`,
                            borderBottomLeftRadius: `${bezel}px`,
                            borderTopRightRadius: imageSideRadiusEnabled
                                ? `${bezel}px`
                                : 0,
                            borderBottomRightRadius: imageSideRadiusEnabled
                                ? `${bezel}px`
                                : 0,
                        },
                        isFocused
                            ? transitionStyles.focusedOverlay
                            : transitionStyles.notFocusedOverlay
                    )}
                >
                    {(!isFocused || showIconsOnFocused) && iconTop && (
                        <Box sx={styleIconTop}>{iconTop}</Box>
                    )}
                    {(!isFocused || showIconsOnFocused) && iconBottom && (
                        <Box sx={styleIconBottom}>{iconBottom}</Box>
                    )}
                    {isFocused && iconFocus && (
                        <Box sx={styleIconFocus}>{iconFocus}</Box>
                    )}
                </Box>
                <Box sx={imageStyle}>
                    <ImageComponent
                        src={src}
                        alt={alt}
                        isFocused={isFocused}
                        width={width}
                    />
                </Box>
            </Box>
        </BaseElement>
    );
}
